import { classAttr, elements, raizDoBloco, repeticoesOpacas, type JsxElement } from './parse';

export interface ProblemaBloco {
	regra: string;
	detalhe: string;
}

/** Classes que respondem à janela — proibidas em qualquer lugar do bloco. */
const JANELA: Array<{ re: RegExp; regra: string }> = [
	{ re: /(?:^|[\s"'`{])((?:sm|md|lg|xl|2xl):[a-z[])/g, regra: 'breakpoint' },
	{ re: /\b\d+(?:\.\d+)?v(?:w|h|min|max)\b/g, regra: 'unidade de janela' },
	{ re: /\b(?:min-)?[hw]-screen\b/g, regra: 'tamanho de tela' },
];

/**
 * Sobreposição pode passar da borda do bloco, mas não sair da página: o bloco
 * mora numa célula da landing page de outra pessoa.
 */
function sobreposicaoSolta(code: string, nos: JsxElement[]): ProblemaBloco[] {
	const out: ProblemaBloco[] = [];

	// no texto da tag, não só no classAttr: className em template literal também conta
	for (const el of nos) {
		if (!/\bfixed\b/.test(code.slice(el.start, el.end))) continue;
		out.push({
			regra: 'sobreposição solta',
			detalhe: `<${el.name}> é fixed — sai do bloco e cobre a página inteira de quem instalou. Use absolute com z-`,
		});
		break;
	}

	if (/\bcreatePortal\b/.test(code)) {
		out.push({
			regra: 'sobreposição solta',
			detalhe: 'createPortal joga o elemento no body da página de quem instalou. Use absolute com z-',
		});
	}

	return out;
}

/** Jeitos de tirar o comprador da página — nenhum vale dentro de um bloco. */
const NAVEGACAO: Array<{ re: RegExp; regra: string; detalhe: string }> = [
	{ re: /window\.open\s*\(/, regra: 'abre janela', detalhe: 'window.open tira o comprador da loja' },
	{ re: /location\s*\.\s*(?:href|assign|replace)\b/, regra: 'redireciona', detalhe: 'mexer em location leva a página embora' },
];

function atributo(el: JsxElement, nome: string): string | null | undefined {
	const attr = el.attrs.find((a) => a.name === nome);
	return attr ? attr.value : undefined;
}

function coordenadasQuebradas(code: string, nos: JsxElement[]): ProblemaBloco[] {
	const tag = (el: JsxElement) => code.slice(el.start, el.end);
	const posicionado = (el: JsxElement) => /\b(?:relative|absolute|fixed|sticky)\b/.test(tag(el));

	const contem = (pai: JsxElement, f: JsxElement) => pai !== f && pai.start < f.start && f.outerEnd <= pai.outerEnd;
	const raiz = nos.length ? nos.reduce((a, b) => (b.outerEnd - b.start > a.outerEnd - a.start ? b : a)) : null;

	const culpados = new Map<string, number>();
	for (const filho of nos) {
		if (!/\babsolute\b/.test(tag(filho))) continue;

		// o pai é o menor elemento que contém este
		const pai = nos.filter((p) => contem(p, filho)).sort((a, b) => a.outerEnd - a.start - (b.outerEnd - b.start))[0];
		if (!pai || pai === raiz || posicionado(pai)) continue;

		culpados.set(pai.loc, (culpados.get(pai.loc) ?? 0) + 1);
	}

	return [...culpados.entries()].slice(0, 3).map(([loc, quantos]) => {
		const pai = nos.find((n) => n.loc === loc)!;
		return {
			regra: 'grupo sem position',
			detalhe: `<${pai.name}> tem ${quantos} filho(s) absolute e não é relative — eles se posicionam pela raiz, não por ele`,
		};
	});
}

export function problemasDoBloco(code: string): ProblemaBloco[] {
	if (!code.trim()) return [];

	const out: ProblemaBloco[] = [];

	for (const { re, regra } of JANELA) {
		const achados = [...new Set([...code.matchAll(re)].map((m) => (m[1] ?? m[0]).trim()))];
		for (const item of achados.slice(0, 4)) {
			out.push({ regra, detalhe: `"${item}" muda com a janela — o bloco tem uma aparência só` });
		}
	}

	for (const { re, regra, detalhe } of NAVEGACAO) {
		if (re.test(code)) out.push({ regra, detalhe });
	}

	let nos: JsxElement[];
	try {
		nos = elements(code);
	} catch {
		return out; // código que nem parseia já é reportado em outro lugar
	}

	// link e formulário existem, mas não podem levar ninguém a lugar nenhum
	let temLink = false;
	for (const el of nos) {
		if (el.name === 'a') {
			temLink = true;
			const href = atributo(el, 'href');
			if (href && href !== '#') {
				out.push({ regra: 'link navega', detalhe: `href="${href}" — use href="#" com preventDefault, ou um <button>` });
			}
			if (atributo(el, 'target') !== undefined) {
				out.push({ regra: 'link com target', detalhe: 'target abre outra aba; o bloco não navega' });
			}
			if (atributo(el, 'onClick') === undefined) {
				out.push({ regra: 'link sem onClick', detalhe: '<a> precisa de onClick com e.preventDefault()' });
			}
		}
		if (el.name === 'form' && atributo(el, 'onSubmit') === undefined) {
			out.push({ regra: 'form sem onSubmit', detalhe: 'enviar recarrega a página — precisa de onSubmit com preventDefault' });
		}
	}

	// tem onClick, mas ninguém segura a navegação
	if (temLink && !/preventDefault\s*\(/.test(code)) {
		out.push({ regra: 'sem preventDefault', detalhe: 'o bloco tem <a> e nenhuma chamada a preventDefault()' });
	}

	out.push(...sobreposicaoSolta(code, nos));
	out.push(...coordenadasQuebradas(code, nos));

	// conteúdo repetido só é editável no painel quando sai de um array literal
	for (const alvo of [...new Set(repeticoesOpacas(code))].slice(0, 3)) {
		out.push({
			regra: 'repetição não editável',
			detalhe: `${alvo}.map(...) não percorre um array literal de itens — o conteúdo repetido não abre no painel`,
		});
	}

	// a raiz não é o primeiro elemento do arquivo: componente auxiliar vem antes
	const raiz = raizDoBloco(code);
	if (!raiz) return out;

	const classe = classAttr(raiz)?.value ?? '';
	const largura = /\bw-\[(\d+)px\]/.exec(classe);
	const altura = /\b(?:min-)?h-\[(\d+)px\]/.exec(classe);

	if (!largura) out.push({ regra: 'raiz sem largura', detalhe: `<${raiz.name}> precisa de w-[Npx] fixo` });
	if (!altura) out.push({ regra: 'raiz sem altura', detalhe: `<${raiz.name}> precisa de h-[Npx] (ou min-h-[Npx])` });
	if (/\bw-full\b/.test(classe)) out.push({ regra: 'raiz com w-full', detalhe: 'a raiz tem tamanho próprio, não preenche o pai' });
	if (/\boverflow-hidden\b/.test(classe)) {
		out.push({ regra: 'raiz corta a sobreposição', detalhe: 'overflow-hidden na raiz corta dropdown, popover e confete — tire dela' });
	}

	return out;
}
